import { Box, Heading } from '@chakra-ui/react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { recordedInterviewEditQuestionScore } from '../../../../../store/student/offer/actions';
import { IOutletContext } from '../../../../../types';
import { IRecordedInterviewUpdateScoresPayload, ScoreTypeEnum } from 'api-paths-cas';
import AllScoresForQuestion, { IScore } from './AllScoresForQuestion';
import QuestionDetailsHeaderButtons from './QuestionDetailsHeaderButtons';
import { EditQuestionScorePayload } from './ScoreDetails';

interface QuestionDetailsHeaderProps {
  questionNo: number;
  question: string;
  numberOfQuestions: number;
}

function QuestionDetailsHeader(props: QuestionDetailsHeaderProps) {
  return (
    <>
      <Heading as="div" color="muted" variant="allcaps" fontSize="sm" lineHeight="6" mb="2">
        Question {props.questionNo} of {props.numberOfQuestions}
      </Heading>

      <Heading as="h3" fontSize="2xl" lineHeight="8" fontWeight="medium" mb="8">
        {props.question}
      </Heading>
    </>
  );
}

function QuestionDetails() {
  const { questionNo: questionNoString, interviewHash } = useParams();
  const { interview, canEditScoresAndAssessment, canViewScoresAndAssessment } = useOutletContext<IOutletContext>();

  const questionsData = interview.scores;
  const questionNo = parseInt(questionNoString ?? '0', 10);

  const theQuestion = questionsData?.find(q => q.questionNo === questionNo);

  const questionNumbers = questionsData?.map(q => q.questionNo);
  const numberOfQuestions = questionNumbers?.length ?? 0;
  const nextQuestionNo = questionNumbers?.find(q => q > questionNo) ?? null;
  const previousQuestionNo = questionNumbers?.reverse()?.find(q => q < questionNo) ?? null;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSave = useCallback(
    ({ type, score, explanation }: EditQuestionScorePayload) => {
      if (!interviewHash) {
        return;
      }
      const payload: IRecordedInterviewUpdateScoresPayload = {
        type,
        score,
        explanation,
        questionNo,
      };
      dispatch(recordedInterviewEditQuestionScore({ interviewHash, payload }));
    },
    [dispatch, interviewHash, questionNo],
  );

  const {
    originalTranscriptScore,
    originalTranscriptExplanation,
    combinedTranscriptText,
    languageProficiencyScore,
    languageProficiencyExplanation,
    answerScore,
    answerExplanation,
    question = '',
  } = theQuestion ?? {};

  const scores: IScore[] = [
    {
      type: ScoreTypeEnum.Score,
      value: answerScore,
      explanation: answerExplanation,
      withIndicator: true,
    },
    {
      type: ScoreTypeEnum.Language,
      value: languageProficiencyScore,
      explanation: languageProficiencyExplanation,
    },
    {
      type: ScoreTypeEnum.Audio,
      value: originalTranscriptScore,
      explanation: originalTranscriptExplanation,
      transcript: combinedTranscriptText,
    },
  ];

  if (!canViewScoresAndAssessment && !canEditScoresAndAssessment) {
    navigate('..');
  }

  return (
    <Box>
      {canEditScoresAndAssessment && (
        <QuestionDetailsHeaderButtons nextQuestionNo={nextQuestionNo} previousQuestionNo={previousQuestionNo} />
      )}
      <QuestionDetailsHeader questionNo={questionNo} question={question} numberOfQuestions={numberOfQuestions} />
      <AllScoresForQuestion scores={scores} onSave={onSave} />
    </Box>
  );
}

export default QuestionDetails;
